export const countSelectedPanelIndices = (selectedByVertex = {}) =>
  Object.values(selectedByVertex || {}).reduce(
    (total, indices) => total + (Array.isArray(indices) ? indices.length : 0),
    0
  );

export class ProductionRunQueue {
  constructor() {
    this.tail = Promise.resolve();
  }

  run = (task) => {
    const result = this.tail.then(task, task);
    this.tail = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  };
}

export const buildIframeProductionSelectionOptions = (settings = {}) => ({
  waitForPanelShadingTextures: false,
  fallbackToAllPanelsWhenSelectionEmpty: true,
  ...(typeof settings.onlySelectedPanels === 'boolean'
    ? {
        onlySelectedPanels: settings.onlySelectedPanels,
        persistSelectionState: false,
      }
    : {}),
});

export const getPersistentIframeProductionSettings = (settings = {}) => {
  const persistentSettings = { ...settings };
  delete persistentSettings.onlySelectedPanels;
  return persistentSettings;
};

export const normalizePowerOptimizerPayload = (optimizer) => {
  if (!optimizer || typeof optimizer !== 'object') return null;

  const pick = (...keys) => {
    for (const key of keys) {
      const value = optimizer[key];
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
  };
  const numberOrNull = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };
  const formatRange = (minimum, maximum) => {
    if (minimum === null && maximum === null) return null;
    if (minimum === null) return String(maximum);
    if (maximum === null) return String(minimum);
    return `${minimum}-${maximum}`;
  };
  const manufacturerValue = pick('manufacturer');
  const manufacturer =
    manufacturerValue && typeof manufacturerValue === 'object'
      ? manufacturerValue.name || manufacturerValue.id
      : manufacturerValue;
  const mpptMinimum = numberOrNull(
    pick('mppt_voltage_min', 'mppt_voltageMin', 'mppt_v_min')
  );
  const mpptMaximum = numberOrNull(
    pick('mppt_voltage_max', 'mppt_voltageMax', 'mppt_v_max')
  );
  const temperatureMinimum = numberOrNull(
    pick('oper_temp_min_c', 'operTempMinC')
  );
  const temperatureMaximum = numberOrNull(
    pick('oper_temp_max_c', 'operTempMaxC')
  );
  const normalized = {
    id: pick('id'),
    company_id: pick('company_id', 'company'),
    status: pick('status'),
    display_name: pick('display_name', 'displayName', 'model', 'name'),
    manufacturer,
    efficiency: numberOrNull(pick('efficiency', 'Efficiency')),
    Voc_max: numberOrNull(pick('Voc_max', 'Voc_Max', 'voc_max')),
    Isc_max: numberOrNull(pick('Isc_max', 'Isc_Max', 'isc_max')),
    efficiency_weighted: numberOrNull(
      pick('efficiency_weighted', 'Efficiency_Weighted')
    ),
    rated_power: numberOrNull(pick('rated_power', 'Rated_Power')),
    mppt_voltage_range:
      pick('mppt_voltage_range', 'MPPT_Voltage_Range') ||
      formatRange(mpptMinimum, mpptMaximum),
    start_voltage: numberOrNull(pick('start_voltage', 'Start_Voltage')),
    operating_temperature_range:
      pick('operating_temperature_range', 'Operating_Temperature_Range') ||
      formatRange(temperatureMinimum, temperatureMaximum),
    Vout_nom: numberOrNull(pick('Vout_nom', 'vout_nom')),
    Vout_min: numberOrNull(pick('Vout_min', 'vout_min')),
    Vout_max: numberOrNull(pick('Vout_max', 'vout_max')),
    Iout_max: numberOrNull(pick('Iout_max', 'iout_max')),
  };

  const pruned = Object.fromEntries(
    Object.entries(normalized).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );
  return Object.keys(pruned).length ? pruned : null;
};

export const resolveRequestedPowerOptimizer = async (
  settings = {},
  retrieve
) => {
  const directOptimizer =
    settings.powerOptimizer && typeof settings.powerOptimizer === 'object'
      ? settings.powerOptimizer
      : settings.power_optimizer &&
          typeof settings.power_optimizer === 'object'
        ? settings.power_optimizer
        : null;
  if (directOptimizer) {
    const normalized = normalizePowerOptimizerPayload(directOptimizer);
    if (!normalized) {
      throw new Error('Power optimizer configuration is invalid');
    }
    return normalized;
  }

  const optimizerIdKey = [
    'companyPowerOptimizerID',
    'companyPowerOptimizerId',
    'powerOptimizerID',
    'powerOptimizerId',
  ].find((key) => Object.prototype.hasOwnProperty.call(settings, key));
  if (!optimizerIdKey) return undefined;

  const optimizerId = settings[optimizerIdKey];
  if (optimizerId === null) return null;
  if (typeof retrieve !== 'function') {
    throw new Error(`Power optimizer configuration ${optimizerId} is unavailable`);
  }

  const normalized = normalizePowerOptimizerPayload(
    await retrieve(optimizerId)
  );
  if (!normalized) {
    throw new Error(`Power optimizer configuration ${optimizerId} is invalid`);
  }
  return normalized;
};

export const runIframeProductionCommand = ({
  queue,
  settings,
  options,
  applySettings,
  runProduction,
}) =>
  queue.run(async () => {
    await applySettings(getPersistentIframeProductionSettings(settings));
    return runProduction(options);
  });

export const resolveProductionPanelSelection = ({
  onlySelectedPanels,
  selectedByVertex,
  fallbackToAllPanelsWhenEmpty = false,
}) => {
  const selectedCount = countSelectedPanelIndices(selectedByVertex);
  const useAllPanels =
    Boolean(onlySelectedPanels) &&
    selectedCount === 0 &&
    fallbackToAllPanelsWhenEmpty;

  return {
    onlySelectedPanels: Boolean(onlySelectedPanels) && !useAllPanels,
    selectedByVertex: useAllPanels ? {} : selectedByVertex || {},
    selectedCount,
    usedAllPanelsFallback: useAllPanels,
  };
};
